import { useEffect, useState } from 'react';
import './LiveClock.css';

interface LiveClockProps {
  orgName: string;
  logoUrl: string | null;
  eventName: string | null;
  circuitName: string | null;
  timezone: string | null;
}

/**
 * Live clock screen — validates async data + dynamic state.
 *
 * Shows current time in the event's circuit timezone (or local time if no event).
 * Updates every second. Glass card container for the clock area.
 */
export default function LiveClock({
  orgName,
  logoUrl,
  eventName,
  circuitName,
  timezone,
}: LiveClockProps) {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Format time in the circuit timezone or local
  const formatOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    ...(timezone ? { timeZone: timezone } : {}),
  };

  const dateOptions: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    ...(timezone ? { timeZone: timezone } : {}),
  };

  let timeString: string;
  let dateString: string;

  try {
    timeString = now.toLocaleTimeString('en-GB', formatOptions);
    dateString = now.toLocaleDateString('en-GB', dateOptions);
  } catch {
    // Invalid timezone — fall back to local
    timeString = now.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
    dateString = now.toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }

  return (
    <div className="live-clock">
      {/* Background glow */}
      <div className="live-clock__bg-glow" />

      <div className="live-clock__container">
        {/* Event name above the clock */}
        {eventName && (
          <p className="live-clock__event-name">{eventName}</p>
        )}

        {/* Glass card with clock */}
        <div className="live-clock__card">
          <time className="live-clock__time">{timeString}</time>
          <p className="live-clock__date">{dateString}</p>

          {circuitName && (
            <p className="live-clock__circuit">{circuitName}</p>
          )}

          {timezone && (
            <p className="live-clock__timezone">
              {timezone.replace(/_/g, ' ')}
            </p>
          )}
        </div>
      </div>

      {/* Org logo in bottom-right corner */}
      {logoUrl && (
        <img
          className="live-clock__org-logo"
          src={logoUrl}
          alt={`${orgName} logo`}
          draggable={false}
        />
      )}
    </div>
  );
}
